import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faPrint, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import { authFetch, formatDate, MODULE_TYPE_LABELS } from '../../utils/http';

export interface StatementData {
  id: string;
  statementNumber: string;
  issuedAt: string;
  issuedByName: string;
  studentName: string;
  studentIdNumber: string;
  learnershipTitle: string;
  saqaId: string;
  nqfLevel: number | null;
  credits: number | null;
  passMark: number;
  averageMark: number;
  overallResult: string;
  modules: { moduleTitle: string; moduleType: number; mark: number; result: string }[];
}

interface Props {
  /** Pass either a loaded statement or an id to fetch. */
  statement?: StatementData | null;
  statementId?: string | null;
  onClose: () => void;
}

const PRINT_CSS = `
@media print {
  body * { visibility: hidden !important; }
  #statement-print-area, #statement-print-area * { visibility: visible !important; }
  #statement-print-area { position: absolute; left: 0; top: 0; width: 100%; box-shadow: none !important; }
  .no-print { display: none !important; }
}`;

/** Printable statement of results. Use the browser's Print dialog to save it as a PDF. */
export default function StatementModal({ statement, statementId, onClose }: Props) {
  const { accessToken } = useAuth();
  const [data, setData] = useState<StatementData | null>(statement ?? null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (statement || !statementId) return;
    (async () => {
      const res = await authFetch<StatementData>(accessToken, `/api/marks/statements/${statementId}`);
      if (res.ok && res.data) setData(res.data);
      else setError(res.message || 'Could not load the statement.');
    })();
  }, [accessToken, statement, statementId]);

  const competent = data?.overallResult === 'Competent';

  return (
    <div className="fixed inset-0 bg-brand-950/40 z-50 flex items-start justify-center px-4 py-8 overflow-y-auto">
      <style>{PRINT_CSS}</style>
      <div className="w-full max-w-3xl">
        <div className="no-print flex justify-end gap-2 mb-3">
          <button
            onClick={() => window.print()}
            disabled={!data}
            className="px-4 py-2 bg-brand-gradient text-white font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50 text-sm flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faPrint} className="h-3.5 w-3.5" />
            Print / Save as PDF
          </button>
          <button onClick={onClose} className="px-4 py-2 bg-white text-brand-950 font-semibold rounded-lg hover:bg-canvas-100 transition-colors text-sm flex items-center gap-2">
            <FontAwesomeIcon icon={faTimes} className="h-3.5 w-3.5" />
            Close
          </button>
        </div>

        <div id="statement-print-area" className="bg-white rounded-2xl shadow-xl p-8 sm:p-10 text-brand-950">
          {!data && !error && (
            <div className="flex justify-center py-16">
              <FontAwesomeIcon icon={faSpinner} className="h-8 w-8 text-brand-600 animate-spin" />
            </div>
          )}
          {error && <p className="text-sm text-red-700">{error}</p>}

          {data && (
            <>
              <div className="text-center border-b-2 border-brand-950 pb-5">
                <p className="text-xs font-bold uppercase tracking-widest text-brand-600">Mokaleng IT</p>
                <h1 className="text-2xl font-bold mt-1">Statement of Results</h1>
                <p className="text-xs text-brand-900/60 mt-1">Statement no. {data.statementNumber}</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 mt-6 text-sm">
                <div>
                  <p className="text-xs text-brand-900/50">Learner</p>
                  <p className="font-semibold">{data.studentName}</p>
                </div>
                <div>
                  <p className="text-xs text-brand-900/50">Learner number</p>
                  <p className="font-semibold">{data.studentIdNumber}</p>
                </div>
                <div className="sm:col-span-2">
                  <p className="text-xs text-brand-900/50">Qualification / learnership</p>
                  <p className="font-semibold">{data.learnershipTitle}</p>
                </div>
                <div>
                  <p className="text-xs text-brand-900/50">SAQA ID</p>
                  <p className="font-semibold">{data.saqaId || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-brand-900/50">NQF level / credits</p>
                  <p className="font-semibold">
                    {data.nqfLevel ?? '—'} / {data.credits ?? '—'}
                  </p>
                </div>
              </div>

              <table className="w-full mt-6 text-sm border border-canvas-300">
                <thead>
                  <tr className="bg-canvas-100 text-left text-xs uppercase tracking-wider text-brand-900/60">
                    <th className="px-3 py-2">Module</th>
                    <th className="px-3 py-2">Component</th>
                    <th className="px-3 py-2 text-right">Mark</th>
                    <th className="px-3 py-2">Result</th>
                  </tr>
                </thead>
                <tbody>
                  {data.modules.map((m, i) => (
                    <tr key={i} className="border-t border-canvas-200">
                      <td className="px-3 py-2">{m.moduleTitle}</td>
                      <td className="px-3 py-2">{MODULE_TYPE_LABELS[m.moduleType]}</td>
                      <td className="px-3 py-2 text-right font-semibold">{m.mark}%</td>
                      <td className={`px-3 py-2 font-semibold ${m.result === 'Competent' ? 'text-green-700' : 'text-red-700'}`}>{m.result}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="flex items-center justify-between mt-6 p-4 border border-canvas-300 rounded-lg">
                <div>
                  <p className="text-xs text-brand-900/50">Average mark</p>
                  <p className="text-lg font-bold">{data.averageMark}%</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-brand-900/50">Overall result</p>
                  <p className={`text-lg font-bold ${competent ? 'text-green-700' : 'text-red-700'}`}>{data.overallResult}</p>
                </div>
              </div>

              <p className="text-xs text-brand-900/50 mt-4">A module is Competent at {data.passMark}% or above.</p>

              <div className="flex items-end justify-between mt-10 text-xs text-brand-900/70">
                <div>
                  <p>Issued {formatDate(data.issuedAt)}</p>
                  <p>Trainer/Assessor: {data.issuedByName}</p>
                </div>
                <div className="text-right">
                  <div className="border-t border-brand-950 w-48 pt-1">Signature</div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
